import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { PageSection, Title, Card, CardTitle, CardBody } from '@patternfly/react-core';
import ContactListGroupInterface from '@app/Interface/ContactListGroupInterface';
import AccordionComponent from '@app/Components/Page/AccordionComponent';
import htmlParser from '@app/utils/htmlParser';

interface ContactListPageProps {
    pageData: PageModel,
}

const ContactListPage: React.FC<ContactListPageProps> = ({ pageData, children }) => {

    /**
     * Creates an AccordionComponent based on the contact group ACF data available for the
     * contact list page template. Each group is its own accordion with a list of contact cards
     * as its content.
     * 
     * @param contactGroups The contact group ACF data to be mapped to the format expected by AccordionComponent
     * @returns AccordionComponent
     */
    const buildContactListAccordions = (contactGroups: ContactListGroupInterface[]) => {
        const contactListAccordions = contactGroups.map(group => {
            // Build the contact Cards for this group
            const contactGroupCards = group.contacts?.map((contact, index) =>
                <Card className="pf-u-mb-md" isFlat isCompact key={index}>
                    <CardTitle>{contact.name} <small>{contact.role}</small></CardTitle>
                    {contact.location &&
                        <CardBody className="pf-u-pb-xs">
                            <strong>Location:</strong> {contact.location}
                        </CardBody>
                    }
                    {contact.email &&
                        <CardBody className="pf-u-pb-xs">
                            <strong>Email:</strong> <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        </CardBody>
                    }
                    {contact.phone &&
                        <CardBody className="pf-u-pb-xs">
                            <strong>Phone:</strong> {contact.phone}
                        </CardBody>
                    }
                </Card>
            ) ?? null;

            // This is the format expected by the AccordionComponent accordions prop
            return {
                title: group.contact_group_name,
                content: <>{contactGroupCards}</>
            };
        });

        return <AccordionComponent accordions={contactListAccordions} />;
    };

    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content">
                    {htmlParser.htmlToJSX(pageData.content ?? '')}
                </div>
                {children}
            </PageSection>

            {pageData.acf.contact_groups &&
                buildContactListAccordions(pageData.acf.contact_groups)
            }
        </>
    )

};

export default ContactListPage;